/**
 * RichTextEditor.jsx
 * TipTap-based rich text editor for the Text Block element.
 *
 * Features (per user spec — option 1c full set):
 * - Bold / Italic / Underline / Strikethrough
 * - Headings (H1 / H2 / H3)
 * - Bullet & Numbered lists
 * - Line breaks / Paragraphs
 * - Text alignment (Left / Center / Right / Justify)
 * - Link
 * - Text color (highlight + foreground)
 * - Font family
 * - Font size (custom mark, since TipTap doesn't ship one)
 * - Insert EJS Variable dropdown (e.g. <%= invoice.number %>)
 *
 * Stores output as HTML (option 2a) — what-you-see-is-what-you-get,
 * embedded inline in the EJS template.
 */
import { useRef } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import { Extension } from "@tiptap/core";
import { StarterKit } from "@tiptap/starter-kit";
import { Underline } from "@tiptap/extension-underline";
import { Link } from "@tiptap/extension-link";
import { TextAlign } from "@tiptap/extension-text-align";
import { TextStyle, Color, FontFamily, FontSize } from "@tiptap/extension-text-style";
import { Highlight } from "@tiptap/extension-highlight";
import {
  Bold, Italic, Underline as UnderlineIcon, Strikethrough,
  List, ListOrdered, AlignLeft, AlignCenter, AlignRight, AlignJustify,
  Link as LinkIcon, Heading1, Heading2, Heading3, Code2, Variable,
  ChevronUp, ChevronDown,
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { FONT_OPTIONS } from "@/lib/elements";
import "./RichTextEditor.css";

/**
 * BlockMargin extension — adds `marginTop` and `marginBottom` attributes to
 * paragraph and heading nodes. These attributes:
 *  - Render as inline `style="margin-top:Xpx;margin-bottom:Ypx"` on export.
 *  - Are parsed back from the same inline styles on HTML re-load.
 *  - Stack with any existing text-align styles set by TextAlign extension.
 */
const BlockMargin = Extension.create({
  name: "blockMargin",
  addOptions() {
    return { types: ["paragraph", "heading"] };
  },
  addGlobalAttributes() {
    return [{
      types: this.options.types,
      attributes: {
        marginTop: {
          default: null,
          parseHTML: (el) => {
            const mt = el.style?.marginTop;
            if (!mt) return null;
            const n = parseInt(mt, 10);
            return Number.isFinite(n) ? n : null;
          },
          renderHTML: (attrs) => {
            if (attrs.marginTop == null) return {};
            return { style: `margin-top: ${attrs.marginTop}px` };
          },
        },
        marginBottom: {
          default: null,
          parseHTML: (el) => {
            const mb = el.style?.marginBottom;
            if (!mb) return null;
            const n = parseInt(mb, 10);
            return Number.isFinite(n) ? n : null;
          },
          renderHTML: (attrs) => {
            if (attrs.marginBottom == null) return {};
            return { style: `margin-bottom: ${attrs.marginBottom}px` };
          },
        },
      },
    }];
  },
});

const FONT_SIZES = ["8px", "9px", "10px", "11px", "12px", "14px", "16px", "18px", "20px", "24px", "28px", "32px", "36px", "48px"];

const COMMON_EJS_VARS = [
  { label: "Invoice #",      value: "invoice.number" },
  { label: "Invoice Date",   value: "invoice.date" },
  { label: "Due Date",       value: "invoice.dueDate" },
  { label: "PO Number",      value: "invoice.poNumber" },
  { label: "Company Name",   value: "company.name" },
  { label: "Company Address", value: "company.address" },
  { label: "Customer Name",  value: "customer.name" },
  { label: "Customer Email", value: "customer.email" },
  { label: "Customer Phone", value: "customer.phone" },
  { label: "Customer Address", value: "customer.address" },
  { label: "Subtotal",       value: "totals.subtotal" },
  { label: "Tax",            value: "totals.tax" },
  { label: "Total",          value: "totals.total" },
  { label: "Notes",          value: "invoice.notes" },
];

function TbBtn({ active, onClick, title, children, testid }) {
  return (
    <button
      type="button"
      title={title}
      data-testid={testid}
      onMouseDown={(e) => {
        e.preventDefault();
        onClick();
      }}
      className={`p-1 rounded text-gray-600 hover:bg-gray-200 transition ${active ? "bg-gray-900 text-white hover:bg-gray-800" : ""}`}
    >
      {children}
    </button>
  );
}

export function RichTextEditor({ value, onChange }) {
  /**
   * Holds the last known selection range so the EJS Variable dropdown
   * can insert at the original cursor position (clicking the dropdown
   * blurs the editor and resets the selection to position 0 otherwise).
   */
  const lastSelectionRef = useRef(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
      }),
      Underline,
      Link.configure({ openOnClick: false, HTMLAttributes: { rel: "noopener noreferrer" } }),
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      TextStyle,
      Color,
      Highlight.configure({ multicolor: true }),
      FontFamily.configure({ types: ["textStyle"] }),
      FontSize,
      BlockMargin,
    ],
    content: value || "",
    onUpdate: ({ editor: ed }) => onChange(ed.getHTML()),
    onSelectionUpdate: ({ editor: ed }) => {
      lastSelectionRef.current = { from: ed.state.selection.from, to: ed.state.selection.to };
    },
  });

  if (!editor) return null;

  const insertEjsVar = (path) => {
    if (!path || !editor) return;
    // Insert the variable as a clean `{{path}}` text token. The export pipeline
    // (ejsGenerator / previewGenerator) and the canvas pill renderer all detect
    // this pattern and convert it to <%= path %> at export time / styled pill
    // at design time. No HTML span is needed any more — TipTap's textStyle mark
    // was stripping data-ejs-path attributes and serializing the literal markup.
    const token = `{{${path}}} `;
    const sel = lastSelectionRef.current;
    if (sel) {
      editor.chain().focus().insertContentAt(sel.from, token).run();
    } else {
      editor.chain().focus().insertContent(token).run();
    }
  };

  /**
   * Convert the currently-highlighted text into a dynamic EJS variable key.
   * Workflow: user highlights some text (the variable path they want to bind),
   * clicks the "Convert to Dynamic" button, and the selection is replaced with
   * a clean `{{path}}` text token. Empty selection → no-op.
   */
  const convertSelectionToDynamic = () => {
    if (!editor) return;
    const { from, to, empty } = editor.state.selection;
    if (empty) {
      window.alert("Select the text you want to convert to a dynamic variable first.");
      return;
    }
    const selectedText = editor.state.doc.textBetween(from, to, " ").trim();
    if (!selectedText) return;
    // sanitize as a JS dotted path — drop everything except letters, digits, dot, underscore
    const path = selectedText.replace(/[^A-Za-z0-9_.[\]]/g, "").replace(/^\.+|\.+$/g, "");
    if (!path) {
      window.alert("Selected text doesn't look like a valid binding path (use letters, digits, dots only).");
      return;
    }
    // Replace selection with the clean token. TipTap inserts it as plain text,
    // so the visible UI shows e.g. "Tax Invoice {{invoiceNo}}" — no raw markup.
    editor.chain().focus().insertContentAt({ from, to }, `{{${path}}}`).run();
  };

  const setLink = () => {
    const prev = editor.getAttributes("link").href;
    const url = window.prompt("URL", prev || "https://");
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  const currentFontFamily = editor.getAttributes("textStyle").fontFamily || "";
  const currentFontSize = editor.getAttributes("textStyle").fontSize || "";
  const currentColor = editor.getAttributes("textStyle").color || "#000000";
  const currentHighlight = editor.getAttributes("highlight").color || "#FFFF00";

  /**
   * Active block node helpers — read/write marginTop/marginBottom on the
   * paragraph or heading at the current cursor. Falls back to paragraph.
   */
  const activeBlockType = () => {
    if (editor.isActive("heading", { level: 1 })) return { type: "heading", attrs: { level: 1 } };
    if (editor.isActive("heading", { level: 2 })) return { type: "heading", attrs: { level: 2 } };
    if (editor.isActive("heading", { level: 3 })) return { type: "heading", attrs: { level: 3 } };
    return { type: "paragraph", attrs: {} };
  };
  const currentMarginTop = editor.getAttributes("paragraph").marginTop ?? editor.getAttributes("heading").marginTop ?? 0;
  const currentMarginBottom = editor.getAttributes("paragraph").marginBottom ?? editor.getAttributes("heading").marginBottom ?? 0;
  const setBlockMargin = (key, val) => {
    const num = val === "" ? null : Math.max(0, Number(val) | 0);
    const block = activeBlockType();
    if (block.type === "heading") {
      editor.chain().focus().updateAttributes("heading", { [key]: num }).run();
    } else {
      editor.chain().focus().updateAttributes("paragraph", { [key]: num }).run();
    }
  };

  return (
    <div className="border border-gray-200 rounded-md bg-white" data-testid="rich-text-editor">
      {/* ── Toolbar Row 1: text styles & headings ── */}
      <div className="flex flex-wrap items-center gap-0.5 px-1 py-1 border-b border-gray-100 bg-gray-50">
        <TbBtn active={editor.isActive("bold")}      onClick={() => editor.chain().focus().toggleBold().run()}      title="Bold">      <Bold className="w-3 h-3" /></TbBtn>
        <TbBtn active={editor.isActive("italic")}    onClick={() => editor.chain().focus().toggleItalic().run()}    title="Italic">    <Italic className="w-3 h-3" /></TbBtn>
        <TbBtn active={editor.isActive("underline")} onClick={() => editor.chain().focus().toggleUnderline().run()} title="Underline"><UnderlineIcon className="w-3 h-3" /></TbBtn>
        <TbBtn active={editor.isActive("strike")}    onClick={() => editor.chain().focus().toggleStrike().run()}    title="Strikethrough"><Strikethrough className="w-3 h-3" /></TbBtn>
        <div className="w-px h-4 bg-gray-300 mx-0.5" />
        <TbBtn active={editor.isActive("heading", { level: 1 })} onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} title="Heading 1"><Heading1 className="w-3 h-3" /></TbBtn>
        <TbBtn active={editor.isActive("heading", { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} title="Heading 2"><Heading2 className="w-3 h-3" /></TbBtn>
        <TbBtn active={editor.isActive("heading", { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} title="Heading 3"><Heading3 className="w-3 h-3" /></TbBtn>
        <div className="w-px h-4 bg-gray-300 mx-0.5" />
        <TbBtn active={editor.isActive("bulletList")}  onClick={() => editor.chain().focus().toggleBulletList().run()}  title="Bullet List">   <List className="w-3 h-3" /></TbBtn>
        <TbBtn active={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()} title="Numbered List"><ListOrdered className="w-3 h-3" /></TbBtn>
        <div className="w-px h-4 bg-gray-300 mx-0.5" />
        <TbBtn active={editor.isActive({ textAlign: "left" })}    onClick={() => editor.chain().focus().setTextAlign("left").run()}    title="Align Left">   <AlignLeft className="w-3 h-3" /></TbBtn>
        <TbBtn active={editor.isActive({ textAlign: "center" })}  onClick={() => editor.chain().focus().setTextAlign("center").run()}  title="Align Center"> <AlignCenter className="w-3 h-3" /></TbBtn>
        <TbBtn active={editor.isActive({ textAlign: "right" })}   onClick={() => editor.chain().focus().setTextAlign("right").run()}   title="Align Right">  <AlignRight className="w-3 h-3" /></TbBtn>
        <TbBtn active={editor.isActive({ textAlign: "justify" })} onClick={() => editor.chain().focus().setTextAlign("justify").run()} title="Justify">      <AlignJustify className="w-3 h-3" /></TbBtn>
        <div className="w-px h-4 bg-gray-300 mx-0.5" />
        <TbBtn active={editor.isActive("link")} onClick={setLink} title="Link"><LinkIcon className="w-3 h-3" /></TbBtn>
      </div>

      {/* ── Toolbar Row 2: font, size, color, EJS var insert ── */}
      <div className="flex flex-wrap items-center gap-1 px-1 py-1 border-b border-gray-100 bg-gray-50">
        <Select value={currentFontFamily} onValueChange={(v) => editor.chain().focus().setFontFamily(v).run()}>
          <SelectTrigger className="h-6 w-28 text-[10px]" data-testid="rte-font-family"><SelectValue placeholder="Font" /></SelectTrigger>
          <SelectContent>
            {FONT_OPTIONS.map((f) => <SelectItem key={f} value={f} className="text-[10px]">{f.split(",")[0]}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={currentFontSize} onValueChange={(v) => editor.chain().focus().setFontSize(v).run()}>
          <SelectTrigger className="h-6 w-14 text-[10px]" data-testid="rte-font-size"><SelectValue placeholder="Size" /></SelectTrigger>
          <SelectContent>
            {FONT_SIZES.map((s) => <SelectItem key={s} value={s} className="text-[10px]">{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <label className="flex items-center gap-0.5 cursor-pointer" title="Text color">
          <Code2 className="w-3 h-3 text-gray-500" />
          <input
            type="color"
            value={currentColor}
            onChange={(e) => editor.chain().focus().setColor(e.target.value).run()}
            className="w-4 h-4 rounded border border-gray-200 cursor-pointer p-0"
            data-testid="rte-text-color"
          />
        </label>
        <label className="flex items-center gap-0.5 cursor-pointer" title="Highlight color">
          <span className="w-3 h-3 inline-block rounded-sm" style={{ background: currentHighlight }} />
          <input
            type="color"
            value={currentHighlight}
            onChange={(e) => editor.chain().focus().toggleHighlight({ color: e.target.value }).run()}
            className="w-4 h-4 rounded border border-gray-200 cursor-pointer p-0"
            data-testid="rte-highlight-color"
          />
        </label>
        <div className="w-px h-4 bg-gray-300 mx-0.5" />
        <TbBtn
          active={false}
          onClick={convertSelectionToDynamic}
          title="Convert highlighted text to a dynamic EJS variable"
          testid="rte-convert-to-dynamic"
        >
          <Variable className="w-3 h-3" />
        </TbBtn>
        <Select value="" onValueChange={insertEjsVar}>
          <SelectTrigger className="h-6 w-28 text-[10px]" data-testid="rte-ejs-insert">
            <SelectValue placeholder="Insert var..." />
          </SelectTrigger>
          <SelectContent>
            {COMMON_EJS_VARS.map((v) => (
              <SelectItem key={v.value} value={v.value} className="text-[10px]">
                <span className="font-mono">{v.label}</span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* ── Toolbar Row 3: per-paragraph vertical margins (1px stepper) ── */}
      <div className="flex flex-wrap items-center gap-2 px-1 py-1 border-b border-gray-100 bg-gray-50">
        <div className="flex items-center gap-1">
          <ChevronUp className="w-3 h-3 text-gray-500" title="Margin Top" />
          <input
            type="number"
            step="1"
            min="0"
            placeholder="0"
            value={currentMarginTop ?? 0}
            onChange={(e) => setBlockMargin("marginTop", e.target.value)}
            className="h-6 w-14 text-[10px] border border-gray-300 rounded px-1"
            data-testid="rte-margin-top"
            title="Top margin (px) — 1px per step"
          />
          <span className="text-[9px] text-gray-400">px</span>
        </div>
        <div className="flex items-center gap-1">
          <ChevronDown className="w-3 h-3 text-gray-500" title="Margin Bottom" />
          <input
            type="number"
            step="1"
            min="0"
            placeholder="0"
            value={currentMarginBottom ?? 0}
            onChange={(e) => setBlockMargin("marginBottom", e.target.value)}
            className="h-6 w-14 text-[10px] border border-gray-300 rounded px-1"
            data-testid="rte-margin-bottom"
            title="Bottom margin (px) — 1px per step"
          />
          <span className="text-[9px] text-gray-400">px</span>
        </div>
        <span className="text-[9px] text-gray-400 ml-2">Margins apply to the active paragraph/heading.</span>
      </div>

      <EditorContent editor={editor} className="rich-text-content px-2 py-1.5" data-testid="rte-content" />
    </div>
  );
}
